import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { BeniUpsell } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export default class BenchmarkInsightTrialService extends Service {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  saveBeniUpsellRecordTask = task({}, async (): Promise<void> => {
    const { beniUpsellRecord } = this;

    if (!beniUpsellRecord) {
      return;
    }

    try {
      await beniUpsellRecord.save();
    } catch (error) {
      this.errorTracking.captureException(error);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
    }
  });

  upsellApiResponseTask = task(
    { restartable: true },
    async (): Promise<BeniUpsell | null> => {
      try {
        const params = {
          typeKey: 'benchmarkinsight/upsell',
          subscription_id: this.hotelAndMarketService.hotel.id ?? null,
        };

        return await this.store.queryRecord<BeniUpsell>(
          'benchmark-insight/upsell',
          params,
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
      }

      return null;
    },
  );

  get beniUpsellRecord(): BeniUpsell | null {
    return this.upsellApiResponseTask.last?.value ?? null;
  }

  get hasRequestedTrial(): boolean {
    return this.beniUpsellRecord?.trialRequested ?? false;
  }

  @action
  async onClickStartTrial(): Promise<void> {
    const { beniUpsellRecord, saveBeniUpsellRecordTask } = this;

    if (!beniUpsellRecord) {
      return;
    }

    const hasConfirmedRequestTrial =
      await this.alerting.showConfirmWarningAlert({
        title: this.intl.t('benchmark_insight.trial.alert.title'),
        text: this.intl.t('benchmark_insight.trial.alert.description'),
        dangerMode: false,
        icon: undefined,
        buttons: {
          cancel: {
            text: this.intl.t('general.cancel'),
          },
          confirm: {
            text: this.intl.t('benchmark_insight.trial.alert.confirm'),
          },
        },
      });

    if (!hasConfirmedRequestTrial) {
      return;
    }

    beniUpsellRecord.trialRequested = true;
    await saveBeniUpsellRecordTask.perform();

    this.usageAnalytics.track('Upsell Got In Touch', {
      eventTrigger: 'upsell-banners',
      product: 'benchmark-insight',
    });
  }

  @action
  openHelpArticle(): void {
    openUrl(
      'https://www.mylighthouse.com/market-intelligence/benchmark-insight?internal_source=app-beni',
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/trial': BenchmarkInsightTrialService;
  }
}
